/// <amd-module name="WorkItemsHub/Utils/WorkItemsGridColumnFactory" />
import { ColumnActionsMode, IColumn } from "OfficeFabric/DetailsList";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { WorkItemTypeColorAndIcon } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getDateInUserTimeZone } from "Wit/Common/DateUtils";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { DefaultNumberFormatOptions } from "Wit/Common/Utilities/FormatUtils";
import { splitAndTrimTags } from "Wit/Filter/Utilities/Utilities";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import { convertWorkItemIdentityRefFromFieldValue, isWorkItemIdentityRef } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { IdentityDisplayMode, IdentityView } from "Wit/UI/IdentityView";
import { IWorkItemStateColorMetadataService, IWorkItemTypeIconMetadataService } from "Wit/UI/Services";
import { TagsList } from "Wit/UI/TagsList";
import { WorkItemState, WorkItemStateAndColor } from "Wit/UI/WorkItemState";
import { createWorkItemCommentCountCell } from "WorkItemsHub/Components/Cells/WorkItemCommentCountCell";
import { createWorkItemDateTimeCell } from "WorkItemsHub/Components/Cells/WorkItemDateTimeCell";
import { createWorkItemIconTitleCell } from "WorkItemsHub/Components/Cells/WorkItemIconTitleCell";
import { createWorkItemSimpleCell } from "WorkItemsHub/Components/Cells/WorkItemSimpleCell";
import { MyActivityDateFieldValues } from "WorkItemsHub/Constants";
import { IWorkItemsGridRow } from "WorkItemsHub/DataContracts/IWorkItemsGridData";
import { RecentActivityConstants } from "WorkItemsHub/Generated/Constants";
import { OnOpenWorkItemHandler } from "WorkItemsHub/Utils/NavigationUtils";
import * as React from "react";

type CreateSimpleCell<T> = (value: T, friendlyStartDateTime?: Date) => JSX.Element;

export interface IWorkItemsGridColumnFactory {
    create(
        pageContext: IVssPageContext,
        projectName: string,
        fieldReferenceName: string,
        fieldFriendlyName: string,
        fieldReferenceNameToIndex: { [key: string]: number },
        friendlyStartDateTime: Date,
        onOpenWorkItem: OnOpenWorkItemHandler,
        isSorted: boolean,
        isSortedDescending: boolean,
        width?: number
    ): IColumn;
}

export class WorkItemsGridColumnFactory implements IWorkItemsGridColumnFactory {
    private static readonly DefaultWidth = 150;
    private static readonly MinWidth = 10;

    private static readonly KnownColumnDefaultWidths: { [key: string]: number } = {
        [CoreFieldRefNames.Id]: 60,
        [CoreFieldRefNames.WorkItemType]: 90,
        [CoreFieldRefNames.Title]: 450,
        [CoreFieldRefNames.AreaPath]: 200,
        [CoreFieldRefNames.IterationPath]: 200,
        [CoreFieldRefNames.AssignedTo]: 200,
        [CoreFieldRefNames.State]: 100,
        [CoreFieldRefNames.Tags]: 200,
        [CoreFieldRefNames.CommentCount]: 75,
        [RecentActivityConstants.MyActivityDetailsField]: 110
    };

    private static readonly DefaultCreateMethod: CreateSimpleCell<string> = (value: string): JSX.Element => {
        return createWorkItemSimpleCell({ text: value });
    };

    public static getDefaultColumnWidth(fieldReferenceName: string): number {
        return WorkItemsGridColumnFactory.KnownColumnDefaultWidths[fieldReferenceName] || WorkItemsGridColumnFactory.DefaultWidth;
    }

    public create(
        pageContext: IVssPageContext,
        projectName: string,
        fieldReferenceName: string,
        fieldFriendlyName: string,
        fieldReferenceNameToIndex: { [key: string]: number },
        friendlyStartDateTime: Date,
        onOpenWorkItem: OnOpenWorkItemHandler,
        isSorted: boolean,
        isSortedDescending: boolean,
        width?: number
    ): IColumn {
        return {
            key: fieldReferenceName,
            name: fieldFriendlyName,
            minWidth: WorkItemsGridColumnFactory.MinWidth,
            calculatedWidth: width,
            isPadded: false,
            isResizable: true,
            isSorted: isSorted,
            isSortedDescending: isSortedDescending,
            columnActionsMode: ColumnActionsMode.clickable,
            onRender: (item: IWorkItemsGridRow, index: number, column: IColumn) =>
                this._createCell(
                    pageContext,
                    projectName,
                    item,
                    fieldReferenceName,
                    fieldReferenceNameToIndex,
                    column,
                    friendlyStartDateTime,
                    onOpenWorkItem
                )
        } as IColumn;
    }

    private _createCell(
        pageContext: IVssPageContext,
        projectName: string,
        row: IWorkItemsGridRow,
        fieldReferenceName: string,
        fieldReferenceNameToIndex: { [key: string]: number },
        column: IColumn,
        friendlyStartDateTime: Date,
        onOpenWorkItem: OnOpenWorkItemHandler
    ): JSX.Element {
        if (fieldReferenceName === CoreFieldRefNames.Title) {
            return this._createTitleCell(pageContext, projectName, row, fieldReferenceNameToIndex, onOpenWorkItem);
        }

        if (fieldReferenceName === CoreFieldRefNames.State) {
            return this._createStateCell(pageContext, projectName, row, fieldReferenceNameToIndex);
        }

        const index = fieldReferenceNameToIndex[fieldReferenceName];
        let fieldValue = row.values[index];

        if (fieldReferenceName === CoreFieldRefNames.Tags) {
            const tags = splitAndTrimTags(fieldValue);
            return fieldValue ? <TagsList tags={tags} className="work-item-tags" /> : null;
        }

        if (fieldReferenceName === CoreFieldRefNames.AssignedTo) {
            return WorkItemsGridColumnFactory._createIdentityCell(pageContext, fieldValue);
        }

        if (fieldReferenceName === CoreFieldRefNames.CommentCount) {
            return Number(fieldValue) !== 0 ? WorkItemsGridColumnFactory._createCommentCountCell(fieldValue) : null;
        }

        let createMethod: CreateSimpleCell<string | number>;
        if (fieldValue instanceof Date) {
            return WorkItemsGridColumnFactory._createDateTimeCell(row.values[index], "{0}", friendlyStartDateTime, false, pageContext);
        }

        if (isWorkItemIdentityRef(fieldValue)) {
            return WorkItemsGridColumnFactory._createIdentityCell(pageContext, fieldValue);
        } else {
            if (fieldReferenceName === RecentActivityConstants.MyActivityDetailsField) {
                fieldValue = MyActivityDateFieldValues[fieldValue];
            }
            if (fieldValue != null && typeof fieldValue !== "string" && typeof fieldValue !== "number") {
                fieldValue = String(fieldValue);
            }

            if (fieldValue !== null && typeof fieldValue === "number") {
                fieldValue = fieldValue.toLocaleString(undefined, DefaultNumberFormatOptions);
            }

            createMethod = WorkItemsGridColumnFactory.DefaultCreateMethod;
        }

        return createMethod(fieldValue, friendlyStartDateTime);
    }

    private _createStateCell(
        pageContext: IVssPageContext,
        projectName: string,
        row: IWorkItemsGridRow,
        fieldReferenceNameToIndex: { [key: string]: number }
    ): JSX.Element {
        const state = row.values[fieldReferenceNameToIndex[CoreFieldRefNames.State]];
        const workItemType = row.values[fieldReferenceNameToIndex[CoreFieldRefNames.WorkItemType]];
        const workItemStateColorMetadataService = pageContext.getService<IWorkItemStateColorMetadataService>(
            "work-item-state-color-metadata-service"
        );
        const stateAndColor: WorkItemStateAndColor | Promise<WorkItemStateAndColor> = workItemStateColorMetadataService.getWorkItemStateColor(
            projectName,
            workItemType,
            state
        );
        if (stateAndColor instanceof Promise) {
            console.warn("Failed to fetch work item state data from the data provider.");
        } else {
            return <WorkItemState data={stateAndColor} hideText={false} />;
        }
    }

    private _createTitleCell(
        pageContext: IVssPageContext,
        projectName: string,
        row: IWorkItemsGridRow,
        fieldReferenceNameToIndex: { [key: string]: number },
        onOpenWorkItem: OnOpenWorkItemHandler
    ): JSX.Element {
        const workItemId = row.values[fieldReferenceNameToIndex[CoreFieldRefNames.Id]];
        const workItemTitle = row.values[fieldReferenceNameToIndex[CoreFieldRefNames.Title]];
        const workItemType = row.values[fieldReferenceNameToIndex[CoreFieldRefNames.WorkItemType]];

        const workItemTypeIconMetadataService = pageContext.getService<IWorkItemTypeIconMetadataService>("work-item-type-icon-metadata-service");
        const icon: WorkItemTypeColorAndIcon | Promise<WorkItemTypeColorAndIcon> = workItemTypeIconMetadataService.getWorkItemIcon(
            projectName,
            workItemType
        );
        if (icon instanceof Promise) {
            console.warn("Failed to fetch work item icon data from data provider.");
        } else {
            return createWorkItemIconTitleCell(pageContext, {
                workItemId,
                workItemTitle,
                projectName,
                workItemType,
                onOpenWorkItem,
                icon
            });
        }
    }

    private static _createDateTimeCell(
        dateTimeString: string,
        contentFormat: string,
        friendlyStartDateTime: Date,
        showFriendlyDateTime: boolean = true,
        pageContext: IVssPageContext
    ): JSX.Element {
        const date = new Date(dateTimeString);
        const isNewBoardsHub = isFeatureEnabled(pageContext, "ms.vss-work-web.new-boards-hub-feature");
        return createWorkItemDateTimeCell({
            dateTime: isNewBoardsHub ? getDateInUserTimeZone(date) : date,
            contentFormat,
            showFriendlyDateTime,
            friendlyStartDateTime
        });
    }

    private static _createIdentityCell(pageContext: IVssPageContext, name: string | WorkItemIdentityRef): JSX.Element {
        const witIdentityRef = convertWorkItemIdentityRefFromFieldValue(pageContext, name);
        const identity = name && witIdentityRef.identityRef;
        return <IdentityView identityRef={identity} viewMode={IdentityDisplayMode.AvatarAndName} size={"extra-small-plus"} />;
    }

    private static _createCommentCountCell(count: string): JSX.Element {
        return createWorkItemCommentCountCell({
            workItemCommentCount: count
        });
    }
}
